import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Package,
  MapPin,
  CreditCard,
  Truck,
  Clock,
  RefreshCw,
  Loader2,
  Copy,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { PaymentEventsSection } from './PaymentEventsSection';
import { formatMoney } from '@/lib/money';

// ── Types ──

interface OrderItem {
  name: string;
  sku?: string;
  quantity: number;
  price: number;
  total: number;
  imageUrl?: string;
}

interface OrderPayment {
  method: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  gateway: string;
  gatewayPaymentId?: string;
  amount: number;
  paidAt?: string;
  refundedAmount?: number;
}

interface ShippingAddress {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
}

interface StatusHistoryEntry {
  status: string;
  note?: string;
  changedBy?: string;
  changedAt: string;
}

type OrderStatus =
  | 'pending_payment'
  | 'confirmed'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

interface Order {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  items: OrderItem[];
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  total: number;
  currency: string;
  couponCode?: string;
  customerNote?: string;
  status: OrderStatus;
  payment: OrderPayment;
  shippingAddress?: ShippingAddress;
  trackingNumber?: string;
  trackingUrl?: string;
  statusHistory?: StatusHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

// ── Helpers ──

const STATUS_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  pending_payment: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled'],
  processing: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: ['refunded'],
  cancelled: [],
  refunded: [],
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending_payment: 'Pending Payment',
  confirmed: 'Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

function formatCents(cents: number, currency = 'USD'): string {
  // Delegate to the single money formatter (cents are minor units).
  return formatMoney(cents, currency, { alwaysDecimals: true });
}

function getOrderStatusBadge(status: OrderStatus) {
  const map: Record<OrderStatus, { label: string; className: string }> = {
    pending_payment: {
      label: 'Pending Payment',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
    confirmed: {
      label: 'Confirmed',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
    processing: {
      label: 'Processing',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    },
    shipped: {
      label: 'Shipped',
      className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    },
    delivered: {
      label: 'Delivered',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    },
    refunded: {
      label: 'Refunded',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
  };
  const entry = map[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  return <Badge className={entry.className}>{entry.label}</Badge>;
}

function getPaymentStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
    paid: {
      label: 'Paid',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
    refunded: {
      label: 'Refunded',
      className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    },
  };
  const entry = map[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  return <Badge className={entry.className}>{entry.label}</Badge>;
}

function formatDateTime(date: string | undefined | null): string {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function getStatusTimelineIcon(status: string) {
  switch (status) {
    case 'pending_payment':
      return <Clock className="h-4 w-4 text-yellow-600" />;
    case 'confirmed':
      return <CheckCircle className="h-4 w-4 text-blue-600" />;
    case 'processing':
      return <RefreshCw className="h-4 w-4 text-orange-600" />;
    case 'shipped':
      return <Truck className="h-4 w-4 text-purple-600" />;
    case 'delivered':
      return <Package className="h-4 w-4 text-green-600" />;
    case 'cancelled':
      return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    case 'refunded':
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    default:
      return <Clock className="text-muted-foreground h-4 w-4" />;
  }
}

// ── Component ──

export default function OrderDetail() {
  const { projectId, orderId } = useParams<{ projectId: string; orderId: string }>();
  const queryClient = useQueryClient();

  const [newStatus, setNewStatus] = useState<string>('');
  const [statusNote, setStatusNote] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundMode, setRefundMode] = useState<'full' | 'partial'>('full');
  const [refundAmountInput, setRefundAmountInput] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch order detail
  const {
    data: orderResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['order', projectId, orderId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/orders/${orderId}`);
      return response.data as { success: boolean; data: Order };
    },
    enabled: Boolean(projectId) && Boolean(orderId),
  });

  const order = orderResponse?.data;

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (payload: {
      status: string;
      note?: string;
      trackingNumber?: string;
      trackingUrl?: string;
    }) => {
      const body: Record<string, string> = { status: payload.status };
      if (payload.note) body.note = payload.note;
      if (payload.trackingNumber) body.trackingNumber = payload.trackingNumber;
      if (payload.trackingUrl) body.trackingUrl = payload.trackingUrl;
      await api.patch(`/projects/${projectId}/ecommerce/orders/${orderId}/status`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', projectId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-dashboard', projectId] });
      setNewStatus('');
      setStatusNote('');
      setTrackingNumber('');
      setTrackingUrl('');
      toast.success('Order status updated');
    },
    onError: () => {
      toast.error('Failed to update order status');
    },
  });

  // Refund mutation — supports partial refunds + optional reason.
  // Body shape matches backend `refundOrderSchema`: amount in smallest
  // currency unit (cents / paise), omitted for a full refund.
  const refundMutation = useMutation({
    mutationFn: async (payload: { amount?: number; reason?: string }) => {
      const { data } = await api.post(
        `/projects/${projectId}/ecommerce/orders/${orderId}/refund`,
        payload
      );
      return data.data as {
        isFullRefund: boolean;
        refundedAmount: number;
        totalRefunded: number;
        orderAmount: number;
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['order', projectId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['ecommerce-dashboard', projectId] });
      setRefundDialogOpen(false);
      setRefundAmountInput('');
      setRefundReason('');
      setRefundMode('full');
      toast.success(result.isFullRefund ? 'Full refund processed' : 'Partial refund processed');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || 'Failed to process refund';
      toast.error(msg);
    },
  });

  const handleSubmitRefund = () => {
    if (refundMode === 'full') {
      refundMutation.mutate({ reason: refundReason || undefined });
      return;
    }
    const parsed = Number(refundAmountInput);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error('Enter a valid refund amount');
      return;
    }
    const amountInSmallest = Math.round(parsed * 100);
    refundMutation.mutate({
      amount: amountInSmallest,
      reason: refundReason || undefined,
    });
  };

  const handleUpdateStatus = () => {
    if (!newStatus) return;
    updateStatusMutation.mutate({
      status: newStatus,
      note: statusNote || undefined,
      trackingNumber: trackingNumber || undefined,
      trackingUrl: trackingUrl || undefined,
    });
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const validNextStatuses = order ? STATUS_TRANSITIONS[order.status] || [] : [];

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          <p className="text-muted-foreground text-sm">Loading order details...</p>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (isError || !order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}/orders`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Order Not Found</h1>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <AlertTriangle className="text-destructive mb-2 h-8 w-8" />
            <p className="text-destructive text-sm">Failed to load order details</p>
            <p className="text-muted-foreground mt-1 text-xs">
              The order may not exist or there was a server error.
            </p>
            <Link to={`/projects/${projectId}/orders`}>
              <Button variant="link" size="sm" className="mt-4">
                Back to Orders
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}/orders`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">Order #{order.orderNumber}</h1>
              {getOrderStatusBadge(order.status)}
            </div>
            <p className="text-muted-foreground text-sm">
              Created {formatDateTime(order.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Main grid: Left (2/3) + Right (1/3) */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column — 2/3 */}
        <div className="space-y-6 lg:col-span-2">
          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Order Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Image</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-center">Qty</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="bg-muted flex h-10 w-10 items-center justify-center rounded">
                            <Package className="text-muted-foreground h-4 w-4" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{item.name}</p>
                        {item.sku && (
                          <p className="text-muted-foreground text-xs">SKU: {item.sku}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {formatCents(item.price, order.currency)}
                      </TableCell>
                      <TableCell className="text-center text-sm">{item.quantity}</TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatCents(item.total, order.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCents(order.subtotal, order.currency)}</span>
              </div>
              {order.discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Discount{order.couponCode ? ` (${order.couponCode})` : ''}
                  </span>
                  <span className="text-green-600">
                    -{formatCents(order.discountAmount, order.currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span>{formatCents(order.taxAmount, order.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span>{formatCents(order.shippingAmount, order.currency)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>{formatCents(order.total, order.currency)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Customer Note */}
          {order.customerNote && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-4 w-4" />
                  Customer Note
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap text-sm">
                  {order.customerNote}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Status History */}
          {order.statusHistory && order.statusHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Status History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {order.statusHistory.map((entry, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="bg-background flex h-8 w-8 items-center justify-center rounded-full border">
                          {getStatusTimelineIcon(entry.status)}
                        </div>
                        {idx < order.statusHistory!.length - 1 && (
                          <div className="bg-border mt-1 w-px flex-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {STATUS_LABELS[entry.status as OrderStatus] || entry.status}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {formatDateTime(entry.changedAt)}
                          </span>
                        </div>
                        {entry.changedBy && (
                          <p className="text-muted-foreground text-xs">by {entry.changedBy}</p>
                        )}
                        {entry.note && (
                          <p className="text-muted-foreground mt-1 text-sm">{entry.note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Update Status */}
              {validNextStatuses.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Update Status</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>New Status</Label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {validNextStatuses.map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Note (optional)</Label>
                      <Textarea
                        placeholder="Add a note about this status change..."
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        rows={1}
                      />
                    </div>
                  </div>
                  {(newStatus === 'shipped' || order.status === 'processing') && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Tracking Number (optional)</Label>
                        <Input
                          placeholder="e.g. 1Z999AA10123456784"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tracking URL (optional)</Label>
                        <Input
                          placeholder="https://tracking.example.com/..."
                          value={trackingUrl}
                          onChange={(e) => setTrackingUrl(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={!newStatus || updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Update Status
                  </Button>
                </div>
              )}

              {validNextStatuses.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  No further status transitions available for this order.
                </p>
              )}

              {/* Refund */}
              {order.payment?.status === 'paid' && order.status !== 'refunded' && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Refund</h3>
                    <p className="text-muted-foreground text-sm">
                      Process a full refund of {formatCents(order.payment.amount, order.currency)}{' '}
                      to the customer.
                    </p>
                    <Button variant="destructive" onClick={() => setRefundDialogOpen(true)}>
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Process Refund
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column — 1/3 */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Customer Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm font-medium">{order.customerName}</p>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm">{order.customerEmail}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleCopy(order.customerEmail, 'email')}
                >
                  {copiedField === 'email' ? (
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
              {order.customerPhone && (
                <p className="text-muted-foreground text-sm">{order.customerPhone}</p>
              )}
            </CardContent>
          </Card>

          {/* Shipping Address */}
          {order.shippingAddress && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-4 w-4" />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-muted-foreground space-y-1 text-sm">
                  {order.shippingAddress.name && <p>{order.shippingAddress.name}</p>}
                  {order.shippingAddress.line1 && <p>{order.shippingAddress.line1}</p>}
                  {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                  <p>
                    {[
                      order.shippingAddress.city,
                      order.shippingAddress.state,
                      order.shippingAddress.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                  {order.shippingAddress.phone && <p>{order.shippingAddress.phone}</p>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4" />
                Payment Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Method</span>
                <span className="capitalize">{order.payment.method}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                {getPaymentStatusBadge(order.payment.status)}
              </div>
              {order.payment.gatewayPaymentId && (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">Gateway ID</span>
                  <div className="flex items-center gap-1">
                    <span className="max-w-[120px] truncate font-mono text-xs">
                      {order.payment.gatewayPaymentId}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleCopy(order.payment.gatewayPaymentId!, 'gatewayId')}
                    >
                      {copiedField === 'gatewayId' ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium">
                  {formatCents(order.payment.amount, order.currency)}
                </span>
              </div>
              {order.payment.paidAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Paid</span>
                  <span>{formatDate(order.payment.paidAt)}</span>
                </div>
              )}
              {order.payment.refundedAmount != null && order.payment.refundedAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Refunded</span>
                  <span className="text-red-600">
                    {formatCents(order.payment.refundedAmount, order.currency)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Events — archived webhook deliveries + live gateway snapshot.
              Fall back to payment.method because payment.gateway is only stamped
              on webhook-confirmed orders; stuck pending_payment orders (the ones
              an admin most needs to debug) would otherwise hide this card. */}
          {(() => {
            const resolvedGateway =
              order.payment.gateway === 'razorpay' || order.payment.gateway === 'stripe'
                ? order.payment.gateway
                : order.payment.method === 'razorpay' || order.payment.method === 'stripe'
                  ? order.payment.method
                  : undefined;
            if (!resolvedGateway || !projectId || !orderId) return null;
            return (
              <PaymentEventsSection
                projectId={projectId}
                orderId={orderId}
                gateway={resolvedGateway}
                gatewayPaymentId={order.payment.gatewayPaymentId}
                orderStatus={order.status}
                paymentStatus={order.payment.status}
              />
            );
          })()}

          {/* Tracking Info */}
          {(order.trackingNumber || order.trackingUrl) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Truck className="h-4 w-4" />
                  Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {order.trackingNumber && (
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">Number</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-xs">{order.trackingNumber}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleCopy(order.trackingNumber!, 'tracking')}
                      >
                        {copiedField === 'tracking' ? (
                          <CheckCircle className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                {order.trackingUrl && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Link</span>
                    <a
                      href={order.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary max-w-[160px] truncate text-xs hover:underline"
                    >
                      Track Shipment
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Refund Dialog — supports full or partial refund + reason */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>
              Order <span className="font-semibold">#{order.orderNumber}</span> — paid{' '}
              <span className="font-semibold">
                {formatCents(order.payment.amount, order.currency)}
              </span>
              {order.payment.refundedAmount ? (
                <>
                  , already refunded{' '}
                  <span className="font-semibold">
                    {formatCents(order.payment.refundedAmount, order.currency)}
                  </span>
                </>
              ) : null}
              . Gateway refunds cannot be undone from here.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={refundMode === 'full' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRefundMode('full')}
              >
                Full refund
              </Button>
              <Button
                type="button"
                variant={refundMode === 'partial' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRefundMode('partial')}
              >
                Partial refund
              </Button>
            </div>

            {refundMode === 'partial' && (
              <div className="space-y-1">
                <Label htmlFor="refund-amount">
                  Amount ({order.currency}) — remaining refundable{' '}
                  {formatCents(
                    order.payment.amount - (order.payment.refundedAmount ?? 0),
                    order.currency
                  )}
                </Label>
                <Input
                  id="refund-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 9.99"
                  value={refundAmountInput}
                  onChange={(e) => setRefundAmountInput(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="refund-reason">Reason (optional)</Label>
              <Textarea
                id="refund-reason"
                rows={3}
                placeholder="Shown in order history and, if enabled, in the customer's refund email."
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmitRefund}
              disabled={refundMutation.isPending}
            >
              {refundMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <AlertTriangle className="mr-2 h-4 w-4" />
              )}
              {refundMode === 'full' ? 'Confirm full refund' : 'Confirm partial refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
